import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { fmtDate, fmtMoney, fmtNumber } from '../lib/format';
import { Button, Dialog, Empty, ErrorState, Field, Input, Ltr, SkeletonRows, useToast } from './ui';

type Gaps = {
  since: string;
  total: number;
  covered: number;
  coverage: number;
  missingCost: Array<{ productId: number; sku: string; name: string; basePrice: number | null; revenue: number; lines: number; invoices: number }>;
  notInCatalog: Array<{ text: string; revenue: number; lines: number; invoices: number; last: string }>;
};
export type QuickProduct = { id: number; sku: string; name: string; price: number | null; cost: number | null };

const num = (s: string) => (s.trim() === '' ? null : Number(s.replace(',', '.')));

/** A product typed by hand → the catalog, with its cost; the typed text is learned for past and future lines. */
export function QuickProductDialog({
  text,
  price,
  canCost,
  onClose,
  onCreated,
}: {
  text: string | null;
  price?: number | null;
  canCost: boolean;
  onClose: () => void;
  onCreated: (p: QuickProduct) => void;
}) {
  const [name, setName] = useState(text ?? '');
  const [sku, setSku] = useState('');
  const [basePrice, setBasePrice] = useState(price != null ? String(price) : '');
  const [cost, setCost] = useState('');
  const [pack, setPack] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const qc = useQueryClient();
  const save = async () => {
    setBusy(true);
    try {
      const p = await api<QuickProduct>('/api/products/quick', {
        body: { name: name.trim(), sku: sku.trim() || null, price: num(basePrice), cost: canCost ? num(cost) : null, unitsPerPack: num(pack), aliasText: text },
      });
      qc.invalidateQueries({ queryKey: ['products'] });
      qc.invalidateQueries({ queryKey: ['profit'] });
      toast.success(`נוסף לקטלוג · ${p.sku}`);
      onCreated(p);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open={text != null}
      onClose={onClose}
      title="הוספה לקטלוג"
      footer={
        <>
          <Button onClick={onClose}>ביטול</Button>
          <Button variant="primary" loading={busy} disabled={!name.trim()} onClick={save}>הוספה</Button>
        </>
      }
    >
      <div className="stack">
        <Field label="שם המוצר" required hint="אפשר לקצר או לתקן — הנוסח מהחשבונית נשמר ומשויך למוצר הזה">
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </Field>
        <div className="row wrap" style={{ gap: 12, alignItems: 'flex-start' }}>
          <Field label="מק״ט" hint="ריק: מספר אוטומטי (M-0001)">
            <Input value={sku} onChange={(e) => setSku(e.target.value)} ltr style={{ width: 140 }} />
          </Field>
          <Field label="מחיר בסיס (לפני מע״מ)">
            <Input value={basePrice} onChange={(e) => setBasePrice(e.target.value)} inputMode="decimal" ltr style={{ width: 120 }} />
          </Field>
          {canCost ? (
            <Field label="מחיר עלות (לפני מע״מ)">
              <Input value={cost} onChange={(e) => setCost(e.target.value)} inputMode="decimal" ltr style={{ width: 120 }} />
            </Field>
          ) : null}
          <Field label="יח׳ בחבילה">
            <Input value={pack} onChange={(e) => setPack(e.target.value)} inputMode="numeric" ltr style={{ width: 90 }} />
          </Field>
        </div>
      </div>
    </Dialog>
  );
}

/** Just the cost of one product (a line in the order editor without a cost). */
export function CostDialog({ product, onClose, onSaved }: { product: { id: number; name: string } | null; onClose: () => void; onSaved: () => void }) {
  return (
    <Dialog open={product != null} onClose={onClose} title="הזנת מחיר עלות">
      <div className="stack">
        <div>{product?.name}</div>
        <div className="muted small">לפני מע״מ, לחבילה כפי שהמוצר מופיע בקטלוג. נכנס גם לרווחיות של החשבוניות שכבר יצאו.</div>
        {product ? <CostInput productId={product.id} onSaved={onSaved} /> : null}
      </div>
    </Dialog>
  );
}

function CostInput({ productId, onSaved }: { productId: number; onSaved: () => void }) {
  const [v, setV] = useState('');
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const save = async () => {
    const cost = num(v);
    if (!cost || cost <= 0) return;
    setBusy(true);
    try {
      await api(`/api/products/${productId}/cost`, { body: { cost } });
      toast.success('העלות נשמרה');
      onSaved();
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
      <Input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && save()} inputMode="decimal" ltr placeholder="עלות" style={{ width: 90 }} aria-label="מחיר עלות" />
      <Button size="sm" loading={busy} disabled={!num(v)} onClick={save}>שמירה</Button>
    </span>
  );
}

/** What keeps the margin from being complete: products sold without a cost, and invoice texts that are not in the catalog. */
export function ProfitGaps() {
  const { data, error, refetch, isLoading } = useQuery({ queryKey: ['profit', 'gaps'], queryFn: () => api<Gaps>('/api/profit/gaps') });
  const [tab, setTab] = useState<'cost' | 'catalog'>('cost');
  const [adding, setAdding] = useState<string | null>(null);
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ['profit'] });
  const missing = data?.missingCost ?? [];
  const typed = data?.notInCatalog ?? [];
  const missingTotal = missing.reduce((s, r) => s + r.revenue, 0);
  const typedTotal = typed.reduce((s, r) => s + r.revenue, 0);

  return (
    <section className="panel">
      <div className="panel-head row wrap" style={{ gap: 10 }}>
        <h2 style={{ margin: 0 }}>השלמת נתוני רווחיות</h2>
        {data ? <span className="muted small">כיסוי {data.coverage}% מהמכירות מאז {fmtDate(data.since)} — כל שורה שמושלמת כאן נכנסת לאחוז, גם בחשבוניות שכבר יצאו</span> : null}
        <span className="spacer" />
        <div className="segmented" role="group" aria-label="סוג">
          <button className={tab === 'cost' ? 'active' : ''} onClick={() => setTab('cost')}>
            בלי עלות ({fmtNumber(missing.length)})
          </button>
          <button className={tab === 'catalog' ? 'active' : ''} onClick={() => setTab('catalog')}>
            לא בקטלוג ({fmtNumber(typed.length)})
          </button>
        </div>
      </div>
      {error ? (
        <ErrorState error={error} onRetry={() => refetch()} />
      ) : (
        <div className="table-wrap" style={{ maxHeight: 420, overflowY: 'auto' }}>
          <table className="table">
            <thead>
              {tab === 'cost' ? (
                <tr>
                  <th>מוצר</th>
                  <th className="num">מכירות</th>
                  <th className="num hide-sm">חשבוניות</th>
                  <th className="num hide-sm">מחיר בסיס</th>
                  <th className="num">מחיר עלות</th>
                </tr>
              ) : (
                <tr>
                  <th>כפי שהוקלד בחשבונית</th>
                  <th className="num">מכירות</th>
                  <th className="num hide-sm">חשבוניות</th>
                  <th className="num hide-sm">אחרונה</th>
                  <th />
                </tr>
              )}
            </thead>
            <tbody>
              {isLoading && !data ? (
                <SkeletonRows rows={5} cols={5} />
              ) : tab === 'cost' ? (
                missing.length === 0 ? (
                  <tr><td colSpan={5}><Empty title="לכל המוצרים שנמכרו יש עלות" /></td></tr>
                ) : (
                  missing.map((r) => (
                    <tr key={r.productId}>
                      <td className="primary">
                        {r.name}
                        <span className="sub"><Ltr>{r.sku}</Ltr></span>
                      </td>
                      <td className="num tabular">{fmtMoney(r.revenue, { round: true })}</td>
                      <td className="num tabular hide-sm">{fmtNumber(r.invoices)}</td>
                      <td className="num tabular hide-sm muted">{r.basePrice == null ? '—' : fmtMoney(r.basePrice)}</td>
                      <td className="num"><CostInput productId={r.productId} onSaved={refresh} /></td>
                    </tr>
                  ))
                )
              ) : typed.length === 0 ? (
                <tr><td colSpan={5}><Empty title="כל השורות שהוקלדו כבר בקטלוג" body={<Link to="/products/matching">שיוך שורות</Link>} /></td></tr>
              ) : (
                typed.map((r) => (
                  <tr key={r.text}>
                    <td className="primary">{r.text}</td>
                    <td className="num tabular">{fmtMoney(r.revenue, { round: true })}</td>
                    <td className="num tabular hide-sm">{fmtNumber(r.invoices)}</td>
                    <td className="num tabular hide-sm muted">{fmtDate(r.last)}</td>
                    <td className="num">
                      <span className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                        <Button size="sm" onClick={() => setAdding(r.text)}>הוספה לקטלוג</Button>
                        <Link to="/products/matching" className="btn ghost sm" title="זה מוצר שכבר בקטלוג בנוסח אחר">שיוך</Link>
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {data && (tab === 'cost' ? missing.length : typed.length) ? (
              <tfoot>
                <tr>
                  <td className="muted small">סה״כ</td>
                  <td className="num tabular strong">{fmtMoney(tab === 'cost' ? missingTotal : typedTotal, { round: true })}</td>
                  <td colSpan={3} />
                </tr>
              </tfoot>
            ) : null}
          </table>
        </div>
      )}
      {adding != null ? (
        <QuickProductDialog
          text={adding}
          canCost
          onClose={() => setAdding(null)}
          onCreated={() => {
            setAdding(null);
            refresh();
          }}
        />
      ) : null}
    </section>
  );
}
